import { Component, type ComponentChildren } from 'preact';

interface Props {
  /** What the user calls this part of the screen, for example "editor". */
  name: string;
  children: ComponentChildren;
}

interface State {
  error: Error | null;
}

/**
 * Keeps a rendering bug in one panel from blanking the whole app. The rest of the screen keeps
 * working, and the panel explains what happened and offers a way back.
 */
export class PanelBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static override getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown): void {
    console.error(`Cue: the ${this.props.name} failed to render.`, error);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div class="panel-error" role="alert">
        <h2>The {this.props.name} stopped working</h2>
        <p>Your cards are saved. Try again, or reload Cue if it keeps happening.</p>
        <pre>{error.message}</pre>
        <div class="button-row">
          <button type="button" class="button small primary" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button type="button" class="button small" onClick={() => window.location.reload()}>
            Reload Cue
          </button>
        </div>
      </div>
    );
  }
}
